import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import NexWordmark from "@/components/NexWordmark";
import { LIFE_LOGO } from "@/lib/assets";
import { Lock, Loader2, ExternalLink, Shield } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PreviewPublic = () => {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [codeInput, setCodeInput] = useState(searchParams.get("code") || "");

  const load = async (code) => {
    setLoading(true);
    setError("");
    try {
      const params = code ? { code } : {};
      const { data } = await axios.get(`${API}/previews/${slug}`, { params });
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail === "Preview not found" ? "This preview link is invalid or expired." : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(searchParams.get("code") || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const submitCode = (e) => {
    e.preventDefault();
    setSearchParams({ code: codeInput });
    load(codeInput);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-nex-gold" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <Shield size={40} className="mx-auto text-red-400 mb-4" />
          <h1 className="serif-head text-3xl mb-3">Preview Unavailable</h1>
          <p className="text-nex-chrome mb-8">{error}</p>
          <Link to="/" className="tick-btn-secondary inline-flex">← Back to NEX</Link>
        </div>
      </div>
    );
  }

  // Access code gate
  if (data?.requires_code) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center px-6">
        <div className="absolute inset-0 blueprint-bg opacity-40 pointer-events-none" />
        <div className="w-full max-w-md relative">
          <div className="flex items-center justify-center gap-4 mb-10">
            <img src={LIFE_LOGO} alt="" className="h-14 w-14 object-contain" />
            <div className="w-px h-12 bg-nex-border" />
            <NexWordmark size="text-3xl" />
          </div>
          <form onSubmit={submitCode} className="card-industrial corner-tick p-8 space-y-6">
            <div className="text-center">
              <Lock size={22} className="text-nex-gold mx-auto mb-3" />
              <div className="mono-label mb-2">// PRIVATE PROTOTYPE PREVIEW</div>
              <h1 className="serif-head text-2xl">{data.business_name}</h1>
              <p className="text-nex-chrome text-sm mt-3">Enter the access code William shared with you.</p>
            </div>
            <input
              data-testid="preview-code-input"
              type="text"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value)}
              placeholder="Access code"
              autoFocus
              className="input-industrial text-center tracking-widest"
            />
            <button type="submit" data-testid="preview-code-submit" className="tick-btn-primary w-full justify-center">
              Unlock Preview
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Unlocked — show the preview
  const hasUrl = data?.preview_url && data.preview_url.startsWith("http");

  return (
    <div className="min-h-screen bg-[#0B0F19]">
      {/* Header bar */}
      <div className="border-b border-white/10 bg-[#0A0E17]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-4">
            <img src={LIFE_LOGO} alt="" className="h-9 w-9 object-contain" />
            <div className="w-px h-8 bg-nex-border" />
            <NexWordmark size="text-xl" />
          </Link>
          <div className="mono-body text-[10px] uppercase tracking-[0.25em] text-nex-gold">
            // Private Preview
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <div className="mb-8">
          <div className="mono-label mb-3">// Prototype in Progress · {data.status?.replace("_", " ")}</div>
          <h1 className="serif-head text-4xl md:text-6xl">{data.business_name}</h1>
          <p className="text-nex-chrome mt-3">Prepared for <span className="text-nex-silver">{data.client_name}</span></p>
        </div>

        {data.notes && (
          <div className="card-industrial p-6 mb-6">
            <div className="mono-label mb-3">// Progress Notes from William</div>
            <p className="text-nex-silver whitespace-pre-wrap leading-relaxed">{data.notes}</p>
          </div>
        )}

        {hasUrl ? (
          <div className="card-industrial corner-tick p-2 md:p-3 mb-6">
            <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 bg-[#0B0F19]">
              <div className="flex items-center gap-3">
                <div className="flex gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                  <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
                </div>
                <span className="mono-body text-[11px] truncate max-w-[400px]">{data.preview_url}</span>
              </div>
              <a href={data.preview_url} target="_blank" rel="noreferrer" data-testid="open-preview-external" className="tick-btn-secondary !py-1.5 !px-3 text-xs">
                <ExternalLink size={12} /> Open in Tab
              </a>
            </div>
            <div className="relative w-full" style={{ height: "80vh" }}>
              <iframe
                data-testid="preview-iframe"
                src={data.preview_url}
                title="prototype preview"
                className="w-full h-full border-0"
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
              />
            </div>
          </div>
        ) : (
          <div className="card-industrial p-10 text-center">
            <div className="mono-label mb-3">// Prototype Coming Soon</div>
            <p className="text-nex-chrome">William is actively building your engine. This page will show your live prototype the moment it's ready.</p>
          </div>
        )}

        <div className="mono-body text-[10px] text-nex-muted text-center pt-4">
          A NEX prototype preview · Learning Institute For Empowerment LLC
        </div>
      </div>
    </div>
  );
};

export default PreviewPublic;
